import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Image } from 'expo-image';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { useActiveBrand } from '../../hooks/useActiveBrand';
import { resolveBrandLogoUrl, type ActiveBrand } from '../../store/brandStore';
import { useLogoAspect } from '../../hooks/useLogoAspect';
import { RediLogo } from './RediLogo';

// Strip-scale mark: shorter than the tile's, capped so a wide wordmark
// never crowds the address it sits above.
const LOGO_HEIGHT = 16;
const LOGO_MAX_WIDTH = 96;

/**
 * Co-brand strip for the insurer-link journey: "redı with {insurer}" plus a
 * hairline divider. Renders null with no active brand — mount it
 * unconditionally, it decides for itself. See src/theme/brand.ts for the
 * accent half of brand morphing (this component is the strip half only).
 */
type BrandStripProps = {
  /**
   * Explicit brand for this placement. `undefined` (prop absent) falls back
   * to the app-wide link brand; `null` means "the model on screen resolved
   * and has no brand", which renders nothing even mid-link-journey.
   */
  brand?: ActiveBrand | null;
};

export function BrandStrip({ brand: brandProp }: BrandStripProps = {}) {
  const { colors } = useTheme();
  const linkBrand = useActiveBrand();
  const brand = brandProp === undefined ? linkBrand : brandProp;
  const logoUrl = brand?.logoUrl ? resolveBrandLogoUrl(brand.logoUrl) : '';
  const { aspect, onLoad } = useLogoAspect(logoUrl);

  if (!brand) return null;

  return (
    <View testID="brand-strip" style={[styles.row, { borderBottomColor: colors.border }]}>
      <RediLogo fontSize={16} />
      <View style={[styles.divider, { backgroundColor: colors.border }]} />
      <Text style={[styles.text, { color: colors.textSecondary }]} numberOfLines={1}>
        {`with ${brand.name}`}
      </Text>
      {logoUrl ? (
        // Fixed height, width from the mark's cached aspect (wordmarks are
        // wide), on a white plate in both themes: partner logos are drawn
        // for white and are not ours to recolour (colors.logoPlate).
        <View
          style={[
            styles.logoPlate,
            { backgroundColor: colors.logoPlate, borderColor: colors.border },
          ]}>
          <Image
            source={{ uri: logoUrl }}
            contentFit="contain"
            onLoad={onLoad}
            accessibilityLabel={brand.name}
            style={{
              width: Math.min(LOGO_MAX_WIDTH, Math.round(LOGO_HEIGHT * aspect)),
              height: LOGO_HEIGHT,
            }}
          />
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    paddingVertical: SPACING.xs,
    paddingHorizontal: SPACING.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  divider: {
    width: StyleSheet.hairlineWidth,
    height: 14,
  },
  text: {
    ...TYPOGRAPHY.caption,
    flexShrink: 1,
  },
  logoPlate: {
    marginLeft: SPACING.xs,
    paddingVertical: SPACING.xxs,
    paddingHorizontal: SPACING.xs,
    borderRadius: BORDER_RADIUS.sm,
    borderWidth: StyleSheet.hairlineWidth,
  },
});
